import { Theme } from '@mui/material/styles';
import { activeRiskValue, isValidRiskValue, RiskFields } from '../../../utils/trade';
import type { SimulateStatus } from './useSimulateTrade';
import { SIMULATE_COPY } from './content';

export const ORDER_SIDES = ['Buy', 'Sell'] as const;
export const ORDER_KINDS = ['Market', 'Limit', 'Stop'] as const;
export type OrderSide = (typeof ORDER_SIDES)[number];
export type OrderKind = (typeof ORDER_KINDS)[number];
export const MARKET_KIND: OrderKind = ORDER_KINDS[0];

/** Wire order type: the side alone for market orders, side + kind otherwise (e.g. SellLimit). */
export const composeOrderType = (side: OrderSide, kind: OrderKind): string => (kind === MARKET_KIND ? side : `${side}${kind}`);

export const splitOrderType = (orderType: string): { side: OrderSide; kind: OrderKind } => {
  const side = ORDER_SIDES.find((s) => orderType.startsWith(s)) ?? ORDER_SIDES[0];
  const kind = ORDER_KINDS.find((k) => k === orderType.slice(side.length)) ?? MARKET_KIND;
  return { side, kind };
};

// Backend parses master expiration as Go layout 2006-01-02T15:04:05.
export const EXPIRATION_WIRE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";
export const EXPIRATION_DISPLAY_FORMAT = 'yyyy-MM-dd HH:mm:ss';

/** Why the simulate trigger is blocked for the current risk inputs; empty when allowed. */
export const simulateRiskBlockReason = (riskType: number, fields: RiskFields): string => {
  if (!riskType) return SIMULATE_COPY.block.riskType;
  return isValidRiskValue(activeRiskValue(riskType, fields)) ? '' : SIMULATE_COPY.block.riskValue;
};

export const SIMULATE_DIALOG_WIDTH_PX = 720;

// Header progress line per request status (0-1).
export const STATUS_PROGRESS: Record<SimulateStatus, number> = { idle: 0.34, submitting: 0.67, success: 1, error: 1 };

// Simulate opens over the Add Copier drawer (zIndex.drawer + 1000), so the dialog sits above it and its popups above the dialog.
export const simulateDialogZ = (theme: Theme) => theme.zIndex.drawer + 2000;
export const simulateOverlayZ = (theme: Theme) => simulateDialogZ(theme) + 1;

// Copier log levels (trade-manager model.LogLevel*) to SoftAlert severities.
export const LOG_SEVERITY: Record<string, 'info' | 'warning' | 'error'> = { info: 'info', warn: 'warning', error: 'error' };
